/**
 * 계측 사진 업로드/삭제 Hook
 * Feature: #395, #411 (오프라인 지원)
 *
 * 계측 항목의 check(점검사진)/result(결과사진) 슬롯에 대한
 * 이미지 업로드 및 삭제를 처리
 *
 * 온라인: 서버 업로드 → updateMeasure로 photo URL 업데이트
 * 오프라인: 로컬 저장 → syncQueue에 photo 항목 추가 + 로컬 캐시 즉시 반영
 */
import { useState, useCallback } from "react";
import { MeasureType, measureTypeValueToGraphQLKey, createLogger } from "@ict/shared";
import { uploadImage, type UploadParams } from "../lib/upload";
import { uploadMeasurePhoto } from "../lib/upload-measure-photo";
import { compressImage } from "../lib/image-compress";
import { useNetworkStatus } from "./useNetworkStatus";

const logger = createLogger({ module: "useMeasurePhoto" });

/** Apollo 쿼리 결과에서 __typename을 재귀적으로 제거 */
function stripTypenames(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key === "__typename") continue;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      result[key] = stripTypenames(value as Record<string, unknown>);
    } else {
      result[key] = value;
    }
  }
  return result;
}

interface UseMeasurePhotoOptions {
  projectId: string;
  docKey: string;
  machineId: string;
  measureType: MeasureType;
  measureIndex: number;
  /** 현재 계측 항목의 전체 데이터 (서버 $set 대응: photo만 보내면 다른 필드 유실) */
  measureData: Record<string, unknown>;
  updateMeasure: (
    type: MeasureType,
    index: number,
    data: Record<string, unknown>
  ) => Promise<{ success: boolean; error?: string }>;
  /** 로컬 캐시만 업데이트 (syncQueue 추가 없음) - 오프라인 사진 촬영 시 이중 큐 방지 */
  updateMeasureLocalOnly?: (
    type: MeasureType,
    index: number,
    data: Record<string, unknown>
  ) => Promise<{ success: boolean; error?: string }>;
}

interface UseMeasurePhotoReturn {
  uploadPhoto: (type: "check" | "result", file: File) => Promise<void>;
  deletePhoto: (type: "check" | "result") => Promise<void>;
  uploadingSlot: "check" | "result" | null;
}

export function useMeasurePhoto({
  projectId,
  docKey,
  machineId,
  measureType,
  measureIndex,
  measureData,
  updateMeasure,
  updateMeasureLocalOnly,
}: UseMeasurePhotoOptions): UseMeasurePhotoReturn {
  const { isOffline } = useNetworkStatus();
  const [uploadingSlot, setUploadingSlot] = useState<"check" | "result" | null>(null);

  const uploadPhoto = useCallback(
    async (type: "check" | "result", file: File) => {
      setUploadingSlot(type);
      try {
        // 이미지 압축
        const compressed = await compressImage(file);

        // 오프라인: 로컬 저장 + syncQueue + 캐시에 로컬 URL 반영
        if (isOffline) {
          const graphqlMeasureType = measureTypeValueToGraphQLKey(measureType);
          const result = await uploadMeasurePhoto(compressed, {
            projectId,
            docKey,
            machineId,
            measureType: graphqlMeasureType,
            measureIndex,
            slot: type,
          });

          if (result.success && result.url) {
            // 로컬 캐시에 로컬 URL을 즉시 반영 (optimistic update)
            // updateMeasureLocalOnly 사용: syncQueue에 measure update를 추가하지 않음
            // (uploadMeasurePhoto가 이미 photo syncQueue 항목을 추가했으므로 이중 큐 방지)
            const cleanData = stripTypenames(measureData);
            const photoFieldName = type === "check" ? "checkUrl" : "resultUrl";
            const existingPhoto = (cleanData.photo as Record<string, unknown>) ?? {};
            if (!updateMeasureLocalOnly) {
              logger.warn("오프라인인데 updateMeasureLocalOnly가 없음 - 이중 큐 위험");
            }
            const localUpdate = updateMeasureLocalOnly ?? updateMeasure;
            await localUpdate(measureType, measureIndex, {
              ...cleanData,
              photo: { ...existingPhoto, [photoFieldName]: result.url },
            });
            logger.info("오프라인 계측 사진 저장 완료", { type, measureType });
          } else {
            logger.error("오프라인 계측 사진 저장 실패", result.error);
          }
          return;
        }

        // 온라인: 서버 업로드
        const uploadParams: UploadParams = {
          file: compressed,
          type: "document",
          projectId,
          docKey,
          machineId,
        };
        const result = await uploadImage(uploadParams);

        if (!result.success || !result.url) {
          logger.error("계측 사진 업로드 실패", result.error);
          return;
        }

        // 서버 $set은 전체 measure를 교체하므로 기존 데이터와 병합 필수
        // __typename 제거: Apollo 캐시 데이터가 mutation data에 오염되는 것 방지
        const cleanData = stripTypenames(measureData);
        const photoFieldName = type === "check" ? "checkUrl" : "resultUrl";
        const existingPhoto = (cleanData.photo as Record<string, unknown>) ?? {};
        const response = await updateMeasure(measureType, measureIndex, {
          ...cleanData,
          photo: { ...existingPhoto, [photoFieldName]: result.url },
        });

        if (!response.success) {
          logger.error("계측 사진 필드 업데이트 실패", response.error);
        }
      } catch (error) {
        logger.error("계측 사진 업로드 중 오류", error);
      } finally {
        setUploadingSlot(null);
      }
    },
    [
      projectId,
      docKey,
      machineId,
      measureType,
      measureIndex,
      measureData,
      updateMeasure,
      updateMeasureLocalOnly,
      isOffline,
    ]
  );

  const deletePhoto = useCallback(
    async (type: "check" | "result") => {
      try {
        // 사진 삭제: 온라인/오프라인 모두 updateMeasure를 통해 처리
        // (오프라인 시 useMeasures의 updateMeasure가 자동으로 로컬 캐시 + syncQueue 처리)
        const cleanData = stripTypenames(measureData);
        const photoFieldName = type === "check" ? "checkUrl" : "resultUrl";
        const existingPhoto = (cleanData.photo as Record<string, unknown>) ?? {};
        const response = await updateMeasure(measureType, measureIndex, {
          ...cleanData,
          photo: { ...existingPhoto, [photoFieldName]: "" },
        });

        if (!response.success) {
          logger.error("계측 사진 삭제 실패", response.error);
        }
      } catch (error) {
        logger.error("계측 사진 삭제 중 오류", error);
      }
    },
    [measureType, measureIndex, measureData, updateMeasure]
  );

  return { uploadPhoto, deletePhoto, uploadingSlot };
}
